import { plural } from '../format';

/**
 * **The page builder's words** (console redesign C2) — the full-screen editor
 * at `#/b/<id>/site/editor`.
 *
 * Most entries are sentences. A handful are **tables**: the name and purpose
 * of every kind of section, the name of every view, the console's word for a
 * slot, an option, a list field. Each table is one entry — a function from
 * the vocabulary's token to words — so a token the console has no word for
 * answers `''` and the screen falls back to the vocabulary's own English
 * label rather than to nothing (`ui/business/builder/names.ts`). The tokens
 * themselves (`services`, `split`, `headline`) never reach the screen.
 */

const TYPES_EN: Record<string, [string, string]> = {
  hero: ['Hero', 'The first thing people see: the name, one line, the way in.'],
  services: ['Services', 'What they offer, with lengths and prices from their catalog.'],
  hours: ['Hours', 'When they are open, from their own week.'],
  story: ['Story', 'Their words about who they are — a paragraph, a picture.'],
  features: ['Highlights', 'A row of short points, each with an icon.'],
  showcase: ['Showcase', 'Photographs with a line each — the place, the work.'],
  gallery: ['Gallery', 'Photographs of the room, the work, the place.'],
  faq: ['Questions', 'Answers to what people always ask.'],
  inquiry: ['Request form', 'A short form for requests — never a booking, never a price.'],
  menu: ['Menu', 'Their dishes and prices, from their catalog.'],
  sailings: ['Sailings', 'Their trips and seats, from their catalog.'],
  stay: ['Rooms', 'Their rooms and nightly rates, from their catalog.'],
  booking: ['Booking', 'Where people book — their calendar and services.'],
  testimonials: ['Testimonials', 'What clients say, in their own words.'],
  cta: ['Call to action', 'One line and one button that sends people somewhere.'],
  team: ['Team', 'The people clients will meet.'],
  location: ['Location', 'Where they are and how to get there.'],
  footer: ['Footer', 'The address, the fine print, the links at the bottom.'],
  notice: ['Notice', 'One line near the top — a closure or an offer.'],
  policies: ['Policies', 'Cancellations, deposits, what to bring.'],
  gift: ['Gift cards', 'Sell gift cards on the site.'],
  shop: ['Shop', 'Their products and prices, from their catalog.'],
  timetable: ['Timetable', 'Their classes by day, from their catalog.'],
  newsletter: ['Newsletter', 'One field and a button to join their list.'],
  pricing: ['Plans', 'Up to four plans side by side, one marked.'],
  category_header: ['Category heading', 'The top of a category page.'],
  collection: ['Collection', 'The items in a category.'],
  item_header: ['Item heading', 'The top of one item’s page.'],
  related: ['Related items', 'Other things people look at next.'],
};

const TYPES_ES: Record<string, [string, string]> = {
  hero: ['Portada', 'Lo primero que se ve: el nombre, una línea y la forma de reservar.'],
  services: ['Servicios', 'Lo que ofrece, con duración y precio de su catálogo.'],
  hours: ['Horario', 'Cuándo abre, según su propia semana.'],
  story: ['Historia', 'Sus palabras sobre quién es: un párrafo y una foto.'],
  features: ['Destacados', 'Una fila de puntos breves, cada uno con un icono.'],
  showcase: ['Escaparate', 'Fotos con una línea cada una: el lugar, el trabajo.'],
  gallery: ['Galería', 'Fotos del local, del trabajo, del lugar.'],
  faq: ['Preguntas', 'Respuestas a lo que siempre se pregunta.'],
  inquiry: ['Formulario de solicitud', 'Un formulario breve: nunca una reserva ni un precio.'],
  menu: ['Menú', 'Sus platos y precios, de su catálogo.'],
  sailings: ['Salidas', 'Sus excursiones y plazas, de su catálogo.'],
  stay: ['Habitaciones', 'Sus habitaciones y tarifas por noche, de su catálogo.'],
  booking: ['Reservas', 'Donde se reserva: su calendario y sus servicios.'],
  testimonials: ['Opiniones', 'Lo que dicen sus clientes, con sus palabras.'],
  cta: ['Llamada a la acción', 'Una línea y un botón que lleva a algún sitio.'],
  team: ['Equipo', 'Las personas que atenderán a los clientes.'],
  location: ['Ubicación', 'Dónde está y cómo llegar.'],
  footer: ['Pie de página', 'La dirección, la letra pequeña, los enlaces del final.'],
  notice: ['Aviso', 'Una línea arriba: un cierre o una oferta.'],
  policies: ['Condiciones', 'Cancelaciones, depósitos, qué traer.'],
  gift: ['Tarjetas de regalo', 'Vender tarjetas de regalo en el sitio.'],
  shop: ['Tienda', 'Sus productos y precios, de su catálogo.'],
  timetable: ['Horario de clases', 'Sus clases por día, de su catálogo.'],
  newsletter: ['Boletín', 'Un campo y un botón para suscribirse.'],
  pricing: ['Planes', 'Hasta cuatro planes lado a lado, uno destacado.'],
  category_header: ['Encabezado de categoría', 'La parte de arriba de una categoría.'],
  collection: ['Colección', 'Los artículos de una categoría.'],
  item_header: ['Encabezado de artículo', 'La parte de arriba de la página de un artículo.'],
  related: ['Artículos relacionados', 'Otras cosas que se miran después.'],
};

const VIEWS_EN: Record<string, string> = {
  cover: 'Cover photo',
  split: 'Photo beside',
  minimal: 'Words only',
  editorial: 'Editorial',
  search: 'With search',
  list: 'List',
  grid: 'Grid',
  cards: 'Cards',
  thumbnails: 'With pictures',
  tabs: 'Tabs',
  groups: 'Grouped',
  'price-table': 'Price table',
  stack: 'Stacked',
  'split-reverse': 'Photo first',
  zigzag: 'Zigzag',
  quote: 'Quotation',
  icons: 'Icons',
  pillars: 'Pillars',
  quiet: 'Quiet',
  numbered: 'Numbered',
  stats: 'Numbers',
  timeline: 'Timeline',
  strip: 'Strip',
  mosaic: 'Mosaic',
  feature: 'One large',
  masonry: 'Masonry',
  filmstrip: 'Filmstrip',
  pills: 'Pills',
  accordion: 'Accordion',
  'two-column': 'Two columns',
  card: 'Card',
  centered: 'Centred',
  classic: 'Classic',
  inline: 'In a line',
  rail: 'Side rail',
  single: 'One at a time',
  logos: 'Logos',
  band: 'Band',
  avatars: 'Portraits',
  one: 'One person',
  profile: 'Profile',
  closure: 'Closure',
  promo: 'Offer',
  footer: 'In the footer',
};

const VIEWS_ES: Record<string, string> = {
  cover: 'Foto de fondo',
  split: 'Foto al lado',
  minimal: 'Solo texto',
  editorial: 'Editorial',
  search: 'Con buscador',
  list: 'Lista',
  grid: 'Cuadrícula',
  cards: 'Tarjetas',
  thumbnails: 'Con fotos',
  tabs: 'Pestañas',
  groups: 'Agrupado',
  'price-table': 'Tabla de precios',
  stack: 'Apilado',
  'split-reverse': 'Foto primero',
  zigzag: 'Zigzag',
  quote: 'Cita',
  icons: 'Iconos',
  pillars: 'Pilares',
  quiet: 'Discreto',
  numbered: 'Numerado',
  stats: 'Cifras',
  timeline: 'Cronología',
  strip: 'Tira',
  mosaic: 'Mosaico',
  feature: 'Una grande',
  masonry: 'Mampostería',
  filmstrip: 'Carrete',
  pills: 'Píldoras',
  accordion: 'Acordeón',
  'two-column': 'Dos columnas',
  card: 'Tarjeta',
  centered: 'Centrado',
  classic: 'Clásico',
  inline: 'En línea',
  rail: 'Columna lateral',
  single: 'Una a una',
  logos: 'Logotipos',
  band: 'Franja',
  avatars: 'Retratos',
  one: 'Una persona',
  profile: 'Perfil',
  closure: 'Cierre',
  promo: 'Oferta',
  footer: 'En el pie',
};

const SLOTS_EN: Record<string, string> = {
  headline: 'Heading',
  sub: 'Under the heading',
  note: 'Line under the heading',
  kicker: 'Small line above',
  cta_label: 'Button',
  cta_target: 'Button goes to',
  headline_accent: 'Words to highlight',
  image: 'Picture',
  align: 'Where the words sit',
  accent_style: 'Highlighted words',
  kicker_style: 'Small line style',
  scrim: 'Shade over the photo',
  search_placement: 'Search bar',
  columns: 'How many across',
  show_price: 'Show prices',
  show_prices: 'Show prices',
  show_duration: 'Show lengths',
  book_verb: 'A book button on each',
  body: 'Text',
  body_html: 'Text with formatting (HTML)',
  link_label: 'Link words',
  link_target: 'Link goes to',
  links: 'Links to other sites',
  images: 'Pictures',
  occasions: 'Occasions to choose from',
  thanks: 'After sending',
  submit_label: 'Button',
  allow_ordering: 'Allow ordering',
  days_shown: 'Days shown',
  default_nights: 'Nights, to start with',
  services: 'Only these services (catalog ids)',
  address: 'Address',
  phone: 'Telephone',
  map: 'Map picture',
  show_hours: 'Show the week in a line',
  directions: 'Offer directions',
  latitude: 'Latitude',
  longitude: 'Longitude',
  in_masthead: 'Address and today’s hours at the top',
  html: 'Fine print (HTML)',
  text: 'The line',
  consent: 'Consent line',
  fine_print: 'Fine print',
  divider: 'Edge above',
  backdrop: 'Photograph behind',
  wash: 'Photograph shade',
  surface: 'Cards and fields',
};

const SLOTS_ES: Record<string, string> = {
  headline: 'Título',
  sub: 'Debajo del título',
  note: 'Línea bajo el título',
  kicker: 'Línea pequeña encima',
  cta_label: 'Botón',
  cta_target: 'El botón lleva a',
  headline_accent: 'Palabras destacadas',
  image: 'Imagen',
  align: 'Dónde va el texto',
  accent_style: 'Palabras destacadas',
  kicker_style: 'Estilo de la línea pequeña',
  scrim: 'Sombra sobre la foto',
  search_placement: 'Buscador',
  columns: 'Cuántos por fila',
  show_price: 'Mostrar precios',
  show_prices: 'Mostrar precios',
  show_duration: 'Mostrar duración',
  book_verb: 'Botón de reservar en cada uno',
  body: 'Texto',
  body_html: 'Texto con formato (HTML)',
  link_label: 'Texto del enlace',
  link_target: 'El enlace lleva a',
  links: 'Enlaces a otros sitios',
  images: 'Imágenes',
  occasions: 'Ocasiones para elegir',
  thanks: 'Tras enviar',
  submit_label: 'Botón',
  allow_ordering: 'Permitir pedidos',
  days_shown: 'Días visibles',
  default_nights: 'Noches, para empezar',
  services: 'Solo estos servicios (ids del catálogo)',
  address: 'Dirección',
  phone: 'Teléfono',
  map: 'Imagen del mapa',
  show_hours: 'Mostrar la semana en una línea',
  directions: 'Ofrecer cómo llegar',
  latitude: 'Latitud',
  longitude: 'Longitud',
  in_masthead: 'Dirección y horario de hoy arriba',
  html: 'Letra pequeña (HTML)',
  text: 'La línea',
  consent: 'Línea de consentimiento',
  fine_print: 'Letra pequeña',
  divider: 'Borde de arriba',
  backdrop: 'Fotografía de fondo',
  wash: 'Sombra de la fotografía',
  surface: 'Tarjetas y campos',
};

const HINTS_EN: Record<string, string> = {
  headline: 'You can also type it right on the page.',
  cta_label: 'Goes to booking unless told otherwise.',
  image: 'An address starting https://.',
  backdrop: 'A full-width photograph behind this band; its words turn light.',
  surface: '“Auto” leaves each kind of section its own look.',
  services: 'Empty offers everything bookable online.',
  link_target: 'A page (page:faq), a section (section:booking) or booking.',
};

const HINTS_ES: Record<string, string> = {
  headline: 'También puede escribirse directamente en la página.',
  cta_label: 'Lleva a reservas salvo que se indique otra cosa.',
  image: 'Una dirección que empiece por https://.',
  backdrop: 'Una fotografía a todo lo ancho detrás de la franja; el texto se aclara.',
  surface: '«Automático» deja a cada tipo de sección su propio aspecto.',
  services: 'Vacío ofrece todo lo que se reserva en línea.',
  link_target: 'Una página (page:faq), una sección (section:booking) o booking.',
};

const OPTIONS_EN: Record<string, string> = {
  left: 'Left',
  center: 'Centre',
  bottom: 'Bottom',
  hero: 'In the hero',
  masthead: 'At the top',
  plain: 'Plain',
  italic: 'Italic',
  pill: 'Pill',
  tracked: 'Spaced capitals',
  none: 'None',
  dusk: 'Dark',
  ridge: 'Mountain ridge',
  creek: 'Creek curve',
  light: 'Light',
  auto: 'Auto',
  flat: 'Flat',
  card: 'Card',
  glass: 'Frosted glass',
  outlined: 'Outlined',
  heart: 'Heart',
  dot: 'Dot',
  dash: 'Dash',
  ink: 'Ink',
  accent: 'Accent',
  'accent-2': 'Second accent',
  mark: 'Symbol',
  wordmark: 'Name',
  centered: 'Centred',
  overlay: 'Over the photo',
  always: 'Always',
  never: 'Never',
  solid: 'Solid',
  gradient: 'Gradient',
  animated: 'Moving',
  paper: 'Page',
  alt: 'Soft',
  night: 'Dark',
  star: 'Star',
  sun: 'Sun',
  moon: 'Moon',
  key: 'Key',
  map: 'Map',
  bed: 'Bed',
  mug: 'Mug',
  fire: 'Fire',
};

const OPTIONS_ES: Record<string, string> = {
  left: 'Izquierda',
  center: 'Centro',
  bottom: 'Abajo',
  hero: 'En la portada',
  masthead: 'Arriba',
  plain: 'Normal',
  italic: 'Cursiva',
  pill: 'Píldora',
  tracked: 'Mayúsculas espaciadas',
  none: 'Ninguno',
  dusk: 'Oscuro',
  ridge: 'Cresta de montaña',
  creek: 'Curva de arroyo',
  light: 'Claro',
  auto: 'Automático',
  flat: 'Plano',
  card: 'Tarjeta',
  glass: 'Cristal esmerilado',
  outlined: 'Contorno',
  heart: 'Corazón',
  dot: 'Punto',
  dash: 'Guion',
  ink: 'Tinta',
  accent: 'Acento',
  'accent-2': 'Segundo acento',
  mark: 'Símbolo',
  wordmark: 'Nombre',
  centered: 'Centrado',
  overlay: 'Sobre la foto',
  always: 'Siempre',
  never: 'Nunca',
  solid: 'Sólido',
  gradient: 'Degradado',
  animated: 'En movimiento',
  paper: 'Página',
  alt: 'Suave',
  night: 'Oscuro',
  star: 'Estrella',
  sun: 'Sol',
  moon: 'Luna',
  key: 'Llave',
  map: 'Mapa',
  bed: 'Cama',
  mug: 'Taza',
  fire: 'Fuego',
};

const FIELDS_EN: Record<string, string> = {
  label: 'Label',
  value: 'Value',
  src: 'Picture address',
  alt: 'What the picture shows',
  caption: 'Caption',
  q: 'Question',
  a: 'Answer',
  url: 'Address',
  icon: 'Icon',
  title: 'Title',
  text: 'Text',
  image: 'Picture address',
  bullets: 'Points',
  link_label: 'Link words',
  link: 'Link goes to',
  link_target: 'Link goes to',
  name: 'Name',
  price: 'Price',
  period: 'Per',
  note: 'Note',
  featured: 'Mark this one',
};

const FIELDS_ES: Record<string, string> = {
  label: 'Etiqueta',
  value: 'Valor',
  src: 'Dirección de la imagen',
  alt: 'Qué muestra la imagen',
  caption: 'Pie de foto',
  q: 'Pregunta',
  a: 'Respuesta',
  url: 'Dirección',
  icon: 'Icono',
  title: 'Título',
  text: 'Texto',
  image: 'Dirección de la imagen',
  bullets: 'Puntos',
  link_label: 'Texto del enlace',
  link: 'El enlace lleva a',
  link_target: 'El enlace lleva a',
  name: 'Nombre',
  price: 'Precio',
  period: 'Por',
  note: 'Nota',
  featured: 'Destacar este',
};

const GROUPS_EN: Record<string, string> = {
  color: 'Colours',
  type: 'Fonts',
  shape: 'Shapes and buttons',
  space: 'Spacing',
  imagery: 'Logo and pictures',
};

const GROUPS_ES: Record<string, string> = {
  color: 'Colores',
  type: 'Tipografía',
  shape: 'Formas y botones',
  space: 'Espaciado',
  imagery: 'Logotipo e imágenes',
};

const SOURCES_EN: Record<string, [string, string, string]> = {
  catalog: [
    'what they sell',
    'The items, lengths and prices come from their catalog. Change one there and it changes here.',
    'Open What they sell →',
  ],
  calendar: [
    'their calendar and services',
    'People book what the catalog offers, at times their calendar has free.',
    'Open What they sell →',
  ],
  hours: [
    'their opening hours',
    'The same week their calendar books against. It comes in from their answers or the staff app.',
    'See their answers →',
  ],
  gift: ['their gift cards', 'Shown when the Gift cards feature is on.', 'Open Setup →'],
  photos: ['their photos', '', 'Open the Website tab →'],
};

const SOURCES_ES: Record<string, [string, string, string]> = {
  catalog: [
    'lo que vende',
    'Los artículos, duraciones y precios salen de su catálogo. Si cambian allí, cambian aquí.',
    'Abrir Lo que vende →',
  ],
  calendar: [
    'su calendario y sus servicios',
    'Se reserva lo que ofrece el catálogo, en las horas libres de su calendario.',
    'Abrir Lo que vende →',
  ],
  hours: [
    'su horario',
    'La misma semana con la que reserva su calendario. Llega de sus respuestas o de la app del personal.',
    'Ver sus respuestas →',
  ],
  gift: [
    'sus tarjetas de regalo',
    'Se muestra cuando la función Tarjetas de regalo está activa.',
    'Abrir Configuración →',
  ],
  photos: ['sus fotos', '', 'Abrir la pestaña Sitio web →'],
};

const REASONS_EN: Record<string, string> = {
  timeout: 'their site did not answer in time',
  'data-unavailable': 'their site did not answer with its details for this address',
  'version-mismatch':
    'the site and this console are different versions — reloading usually fixes it',
};

const REASONS_ES: Record<string, string> = {
  timeout: 'su sitio no respondió a tiempo',
  'data-unavailable': 'su sitio no respondió con sus datos para esta dirección',
  'version-mismatch': 'el sitio y esta consola son versiones distintas; recargar suele arreglarlo',
};

export const builderEn = {
  /* ---- tables ------------------------------------------------------- */
  'builder.typeName': ({ type }: { type: string }) => TYPES_EN[type]?.[0] ?? '',
  'builder.typePurpose': ({ type }: { type: string }) => TYPES_EN[type]?.[1] ?? '',
  'builder.viewName': ({ view }: { view: string }) => VIEWS_EN[view] ?? '',
  'builder.slotName': ({ slot }: { slot: string }) => SLOTS_EN[slot] ?? '',
  'builder.slotHint': ({ slot }: { slot: string }) => HINTS_EN[slot] ?? '',
  'builder.optionName': ({ option }: { option: string }) => OPTIONS_EN[option] ?? '',
  'builder.fieldName': ({ field }: { field: string }) => FIELDS_EN[field] ?? '',
  'builder.tone': ({ tone }: { tone: string }) => OPTIONS_EN[tone] ?? '',
  'builder.presetName': ({ preset }: { preset: string }) =>
    ({ serene: 'Serene', gilded: 'Gilded', coastal: 'Coastal', bistro: 'Bistro' })[preset] ?? '',
  'builder.motionName': ({ motion }: { motion: string }) =>
    ({ none: 'Still', reveal: 'Reveal on scroll' })[motion] ?? '',
  'builder.look.group': ({ group }: { group: string }) => GROUPS_EN[group] ?? '',
  'builder.source': ({ source }: { source: string }) => SOURCES_EN[source]?.[0] ?? '',

  /* ---- the frame ---------------------------------------------------- */
  'builder.region': 'Page builder',
  'builder.back': '← Back',
  'builder.backTo': ({ name }: { name: string }) => `← ${name}`,
  'builder.website': 'Website',
  'builder.state.unpublished': 'unpublished changes',
  'builder.state.clean': 'as published',
  'builder.rail': 'Page structure',
  'builder.close': 'Close',
  'builder.add': '+ Add a section',
  'builder.page.pick': 'Page',
  'builder.page.home': 'Home page',
  'builder.page.named': ({ title }: { title: string }) => `Page: ${title}`,
  'builder.page.new': '+ New page…',
  'builder.page.catalogView': 'A catalog page (view only)',
  'builder.page.catalogNote':
    'This page is made from what they sell: it can be looked at here, and its content is changed in the catalog.',
  'builder.page.backHome': 'Back to the home page',
  'builder.device.label': 'Screen size',
  'builder.device.desktop': 'Desktop',
  'builder.device.tablet': 'Tablet',
  'builder.device.phone': 'Phone',
  'builder.undo': 'Undo',
  'builder.redo': 'Redo',
  'builder.undoKeys': 'Undo (Ctrl+Z)',
  'builder.redoKeys': 'Redo (Ctrl+Shift+Z)',
  'builder.save.saving': 'Saving…',
  'builder.save.saved': 'Draft saved on this computer',
  'builder.save.failed': 'This browser would not keep the draft — keep this tab open, or publish.',
  'builder.save.clean': 'No unpublished changes',
  'builder.more.label': 'More',
  'builder.more.pages': 'Pages and menu bar…',
  'builder.more.pagesTitle': 'Pages and menu bar',
  'builder.more.css': 'Custom stylesheet…',
  'builder.more.cssTitle': 'Custom stylesheet and formatting rules',
  'builder.more.templates': 'Start from a template…',
  'builder.more.discard': 'Discard unpublished changes',
  'builder.nothingToDiscard': 'There are no unpublished changes.',
  'builder.note.dismiss': 'Dismiss',
  'builder.restoredDraft': ({ when }: { when: string }) =>
    `Picked up the unpublished draft saved on this computer${when === '' ? '' : ` (${when})`}.`,
  'builder.restoredStale':
    'The site has been published since it was started, so it may undo those changes.',
  'builder.published': ({ version }: { version: number }) =>
    `Published — version ${String(version)} is live.`,
  'builder.onlyOne': ({ name }: { name: string }) =>
    `A page has one ${name} section. Add a different kind instead.`,
  'builder.activity.check': 'checked the website design before publishing',

  /* ---- the outline -------------------------------------------------- */
  'builder.outline.label': ({ page }: { page: string }) => `Sections on the ${page}`,
  'builder.outline.empty': 'Nothing on this page yet. Add a section to start it.',
  'builder.outline.pinned': 'pinned',
  'builder.outline.offPage': 'not for this page',
  'builder.outline.notShowing': 'nothing to show',
  'builder.outline.keys':
    'Drag to reorder. With the keyboard: arrow keys move between sections, Enter selects, Alt+↑ or Alt+↓ moves one.',

  /* ---- the inspector ------------------------------------------------ */
  'builder.inspector.label': 'Selected section',
  'builder.inspector.none': 'Click a section on the page, or in the list, to change it.',
  'builder.inspector.pinned': 'Pinned: it can move but not be removed — booking is the site’s job.',
  'builder.inspector.offPage': ({ name }: { name: string }) =>
    `${name} does not belong on this page. It is kept as stored, and publishing will be refused until it is removed or moved.`,
  'builder.inspector.hiddenWords':
    'Nothing shows on the page yet — give it some words or pictures below.',
  'builder.inspector.hiddenData':
    'Nothing shows on the page yet: it fills itself from their data, and there is none for it yet.',
  'builder.inspector.arrange': 'Arrange this section',
  'builder.inspector.onlyOne': 'A page has one of these.',
  'builder.inspector.looks': 'How it looks',
  'builder.inspector.background': 'Background',
  'builder.inspector.bandMore': 'Edge, photograph and cards',
  'builder.inspector.says': 'What it says',
  'builder.inspector.nothingToSay': 'This section has nothing to write — it is all their data.',
  'builder.inspector.photos': 'Pictures here are the ones listed below.',
  'builder.inspector.photosGo': 'Their photos are on the Website tab →',
  'builder.action.up': '↑ Move up',
  'builder.action.down': '↓ Move down',
  'builder.action.duplicate': 'Duplicate',
  'builder.action.remove': 'Remove',
  'builder.bind.title': ({ source }: { source: string }) => `Filled from ${source}`,
  'builder.bind.hint': ({ source }: { source: string }) => SOURCES_EN[source]?.[1] ?? '',
  'builder.bind.go': ({ source }: { source: string }) => SOURCES_EN[source]?.[2] ?? '',
  'builder.slot.default': 'default',
  'builder.slot.defaultIs': ({ value }: { value: string }) => `${value} (default)`,
  'builder.slot.none': '— none',
  'builder.list.empty': 'None yet.',
  'builder.list.add': ({ label }: { label: string }) => `+ Add to ${label}`,
  'builder.list.up': ({ label, n }: { label: string; n: number }) =>
    `Move ${label} ${String(n)} up`,
  'builder.list.down': ({ label, n }: { label: string; n: number }) =>
    `Move ${label} ${String(n)} down`,
  'builder.list.remove': ({ label, n }: { label: string; n: number }) =>
    `Remove ${label} ${String(n)}`,
  'builder.list.oneALine': 'One per line.',
  'builder.remove.title': ({ name }: { name: string }) => `Remove the ${name} section?`,
  'builder.remove.consequence': ({ name }: { name: string }) =>
    `The ${name} section comes off this draft. Nothing changes on their live site until you publish, and Undo brings it back.`,
  'builder.remove.go': 'Remove it',

  /* ---- the gallery -------------------------------------------------- */
  'builder.gallery.title': 'Add a section',
  'builder.gallery.lead':
    'Sections marked “from their data” fill themselves from what they sell, their hours and their calendar. You only choose how they look.',
  'builder.gallery.add': ({ name }: { name: string }) => `Add ${name}`,
  'builder.gallery.fromData': 'from their data',
  'builder.starter.question': 'A question people ask?',
  'builder.starter.answer': 'Their answer.',
  'builder.starter.who': 'A client',
  'builder.starter.quote': 'Their words go here.',
  'builder.starter.feature': ({ n }: { n: number }) => `Point ${String(n)}`,
  'builder.starter.featureText': 'One short line about it.',
  'builder.starter.panel': ({ n }: { n: number }) => `Title ${String(n)}`,
  'builder.starter.panelText': 'A line about this picture.',
  'builder.starter.plan': ({ n }: { n: number }) => `Plan ${String(n)}`,
  'builder.starter.body': 'A few sentences in their words.',
  'builder.starter.notice': 'One line for their visitors.',

  /* ---- look --------------------------------------------------------- */
  'builder.look.title': 'Look',
  'builder.look.presets': 'Style',
  'builder.look.open': 'Colours, fonts and shapes…',
  'builder.look.drawer': 'Colours, fonts and shapes',
  'builder.look.words': 'Name and tagline',
  'builder.look.siteTitleHint': 'Empty uses the business’s own name.',
  'builder.look.presetSays': ({ value }: { value: string }) => `The style’s own: ${value}.`,

  /* ---- pages -------------------------------------------------------- */
  'builder.pages.newTitle': 'A new page',
  'builder.pages.slugHint': ({ slug }: { slug: string }) =>
    `Its address: /p/${slug}. Lower-case letters, digits and hyphens.`,
  'builder.pages.titleOf': ({ slug }: { slug: string }) => `Title of /p/${slug}`,
  'builder.pages.open': 'Arrange this page',
  'builder.pages.new': '+ New page…',
  'builder.pages.bar': 'Menu bar',

  /* ---- the canvas --------------------------------------------------- */
  'builder.canvas.title': 'Their site, as the draft would look',
  'builder.canvas.connecting': 'Opening their site…',
  'builder.canvas.offTitle': 'The live picture of their site can’t be shown right now.',
  'builder.canvas.noAddress':
    'This console does not know where their site is served from, so there is nothing to frame.',
  'builder.canvas.failed': ({ reason }: { reason: string }) =>
    `It did not load: ${REASONS_EN[reason] ?? reason}.`,
  'builder.canvas.keepGoing':
    'The list of sections and the panel on the right still work, and nothing is lost — changes are kept on this computer until you publish.',
  'builder.canvas.retry': 'Try again',
  'builder.canvas.editRefused': 'That text can’t be changed here — use the panel on the right.',
  'builder.canvas.external': 'Links to other sites don’t open in the builder.',
  'builder.canvas.noSuchPage': 'That link goes to a page this draft does not have.',
  'builder.canvas.renderFailed':
    'The site could not draw that change; it shows the last version it could.',

  /* ---- publish, versions, templates, discard ------------------------ */
  'builder.publish.open': 'Publish',
  'builder.publish.title': ({ name }: { name: string }) => `Publish ${name}’s website?`,
  'builder.publish.checking': 'Checking the page the way publishing will…',
  'builder.publish.refused': 'It can’t be published yet:',
  'builder.publish.clean': 'Checked: nothing to fix.',
  'builder.publish.warnings': 'It will be published, with these notes:',
  'builder.publish.cleaned': ({ count }: { count: number }) =>
    `${String(count)} ${plural(count, 'block of formatted text', 'blocks of formatted text')} will be cleaned:`,
  'builder.publish.cleanedZone': ({ removed }: { removed: string }) => `taken out: ${removed}`,
  'builder.publish.note': 'A short note about this change',
  'builder.publish.noteHint': 'Kept with the version, so whoever restores one knows what it was.',
  'builder.publish.consequence':
    'Every guest sees this on their next page load — there is nothing to build or wait for. The version it replaces is kept and can be restored.',
  'builder.publish.go': 'Publish now',
  'builder.versions.open': 'Versions',
  'builder.versions.title': 'Versions',
  'builder.versions.live': 'Live now',
  'builder.versions.replacesDraft': 'The unpublished changes in the builder are replaced by it.',
  'builder.templates.lead':
    'A template replaces the whole draft — every page and the menu bar. Nothing changes on their site until you publish, and Undo brings the draft back.',
  'builder.templates.pages': ({ pages }: { pages: string }) => `Comes with pages: ${pages}.`,
  'builder.templates.consequence':
    'The whole draft is replaced by the template. Nothing changes on their site until you publish, and Undo brings the draft back.',
  'builder.discard.title': 'Discard the unpublished changes?',
  'builder.discard.consequence':
    'The builder goes back to what is published, and the draft on this computer is deleted. Undo still brings it back until you leave.',
  'builder.discard.go': 'Discard them',
};

export const builderEs: typeof builderEn = {
  /* ---- tablas ------------------------------------------------------- */
  'builder.typeName': ({ type }: { type: string }) => TYPES_ES[type]?.[0] ?? '',
  'builder.typePurpose': ({ type }: { type: string }) => TYPES_ES[type]?.[1] ?? '',
  'builder.viewName': ({ view }: { view: string }) => VIEWS_ES[view] ?? '',
  'builder.slotName': ({ slot }: { slot: string }) => SLOTS_ES[slot] ?? '',
  'builder.slotHint': ({ slot }: { slot: string }) => HINTS_ES[slot] ?? '',
  'builder.optionName': ({ option }: { option: string }) => OPTIONS_ES[option] ?? '',
  'builder.fieldName': ({ field }: { field: string }) => FIELDS_ES[field] ?? '',
  'builder.tone': ({ tone }: { tone: string }) => OPTIONS_ES[tone] ?? '',
  'builder.presetName': ({ preset }: { preset: string }) =>
    ({ serene: 'Sereno', gilded: 'Dorado', coastal: 'Costero', bistro: 'Bistró' })[preset] ?? '',
  'builder.motionName': ({ motion }: { motion: string }) =>
    ({ none: 'Quieto', reveal: 'Aparece al desplazarse' })[motion] ?? '',
  'builder.look.group': ({ group }: { group: string }) => GROUPS_ES[group] ?? '',
  'builder.source': ({ source }: { source: string }) => SOURCES_ES[source]?.[0] ?? '',

  /* ---- el marco ----------------------------------------------------- */
  'builder.region': 'Editor de páginas',
  'builder.back': '← Volver',
  'builder.backTo': ({ name }: { name: string }) => `← ${name}`,
  'builder.website': 'Sitio web',
  'builder.state.unpublished': 'cambios sin publicar',
  'builder.state.clean': 'como está publicado',
  'builder.rail': 'Estructura de la página',
  'builder.close': 'Cerrar',
  'builder.add': '+ Añadir una sección',
  'builder.page.pick': 'Página',
  'builder.page.home': 'Página de inicio',
  'builder.page.named': ({ title }: { title: string }) => `Página: ${title}`,
  'builder.page.new': '+ Página nueva…',
  'builder.page.catalogView': 'Una página del catálogo (solo ver)',
  'builder.page.catalogNote':
    'Esta página se hace con lo que vende: aquí se puede mirar, y su contenido se cambia en el catálogo.',
  'builder.page.backHome': 'Volver a la página de inicio',
  'builder.device.label': 'Tamaño de pantalla',
  'builder.device.desktop': 'Escritorio',
  'builder.device.tablet': 'Tableta',
  'builder.device.phone': 'Teléfono',
  'builder.undo': 'Deshacer',
  'builder.redo': 'Rehacer',
  'builder.undoKeys': 'Deshacer (Ctrl+Z)',
  'builder.redoKeys': 'Rehacer (Ctrl+Mayús+Z)',
  'builder.save.saving': 'Guardando…',
  'builder.save.saved': 'Borrador guardado en este ordenador',
  'builder.save.failed':
    'Este navegador no quiso guardar el borrador: deje esta pestaña abierta o publique.',
  'builder.save.clean': 'Sin cambios por publicar',
  'builder.more.label': 'Más',
  'builder.more.pages': 'Páginas y barra de menú…',
  'builder.more.pagesTitle': 'Páginas y barra de menú',
  'builder.more.css': 'Hoja de estilos propia…',
  'builder.more.cssTitle': 'Hoja de estilos propia y reglas de formato',
  'builder.more.templates': 'Empezar con una plantilla…',
  'builder.more.discard': 'Descartar los cambios sin publicar',
  'builder.nothingToDiscard': 'No hay cambios sin publicar.',
  'builder.note.dismiss': 'Cerrar',
  'builder.restoredDraft': ({ when }: { when: string }) =>
    `Se recuperó el borrador sin publicar guardado en este ordenador${when === '' ? '' : ` (${when})`}.`,
  'builder.restoredStale':
    'El sitio se ha publicado después de empezarlo, así que podría deshacer esos cambios.',
  'builder.published': ({ version }: { version: number }) =>
    `Publicado: la versión ${String(version)} está en línea.`,
  'builder.onlyOne': ({ name }: { name: string }) =>
    `Una página lleva una sola sección ${name}. Añada otro tipo.`,
  'builder.activity.check': 'revisó el diseño del sitio antes de publicarlo',

  /* ---- el esquema --------------------------------------------------- */
  'builder.outline.label': ({ page }: { page: string }) => `Secciones de ${page}`,
  'builder.outline.empty': 'Todavía no hay nada en esta página. Añada una sección para empezar.',
  'builder.outline.pinned': 'fija',
  'builder.outline.offPage': 'no es para esta página',
  'builder.outline.notShowing': 'nada que mostrar',
  'builder.outline.keys':
    'Arrastre para reordenar. Con el teclado: las flechas pasan de una sección a otra, Intro la elige y Alt+↑ o Alt+↓ la mueve.',

  /* ---- el panel ----------------------------------------------------- */
  'builder.inspector.label': 'Sección elegida',
  'builder.inspector.none': 'Haga clic en una sección de la página, o de la lista, para cambiarla.',
  'builder.inspector.pinned':
    'Fija: se puede mover pero no quitar; las reservas son el trabajo del sitio.',
  'builder.inspector.offPage': ({ name }: { name: string }) =>
    `${name} no corresponde a esta página. Se conserva tal como está, y la publicación se rechazará hasta que se quite o se mueva.`,
  'builder.inspector.hiddenWords':
    'Todavía no se ve nada en la página: póngale texto o imágenes abajo.',
  'builder.inspector.hiddenData':
    'Todavía no se ve nada en la página: se llena con sus datos, y aún no hay ninguno para ella.',
  'builder.inspector.arrange': 'Ordenar esta sección',
  'builder.inspector.onlyOne': 'Una página lleva solo una.',
  'builder.inspector.looks': 'Cómo se ve',
  'builder.inspector.background': 'Fondo',
  'builder.inspector.bandMore': 'Borde, fotografía y tarjetas',
  'builder.inspector.says': 'Qué dice',
  'builder.inspector.nothingToSay': 'Esta sección no tiene texto que escribir: son todo sus datos.',
  'builder.inspector.photos': 'Las imágenes de aquí son las de la lista de abajo.',
  'builder.inspector.photosGo': 'Sus fotos están en la pestaña Sitio web →',
  'builder.action.up': '↑ Subir',
  'builder.action.down': '↓ Bajar',
  'builder.action.duplicate': 'Duplicar',
  'builder.action.remove': 'Quitar',
  'builder.bind.title': ({ source }: { source: string }) => `Se llena con ${source}`,
  'builder.bind.hint': ({ source }: { source: string }) => SOURCES_ES[source]?.[1] ?? '',
  'builder.bind.go': ({ source }: { source: string }) => SOURCES_ES[source]?.[2] ?? '',
  'builder.slot.default': 'predeterminado',
  'builder.slot.defaultIs': ({ value }: { value: string }) => `${value} (predeterminado)`,
  'builder.slot.none': '— ninguno',
  'builder.list.empty': 'Todavía ninguno.',
  'builder.list.add': ({ label }: { label: string }) => `+ Añadir a ${label}`,
  'builder.list.up': ({ label, n }: { label: string; n: number }) => `Subir ${label} ${String(n)}`,
  'builder.list.down': ({ label, n }: { label: string; n: number }) =>
    `Bajar ${label} ${String(n)}`,
  'builder.list.remove': ({ label, n }: { label: string; n: number }) =>
    `Quitar ${label} ${String(n)}`,
  'builder.list.oneALine': 'Uno por línea.',
  'builder.remove.title': ({ name }: { name: string }) => `¿Quitar la sección ${name}?`,
  'builder.remove.consequence': ({ name }: { name: string }) =>
    `La sección ${name} sale de este borrador. Nada cambia en su sitio hasta que publique, y Deshacer la devuelve.`,
  'builder.remove.go': 'Quitarla',

  /* ---- la galería --------------------------------------------------- */
  'builder.gallery.title': 'Añadir una sección',
  'builder.gallery.lead':
    'Las secciones marcadas «con sus datos» se llenan solas con lo que vende, su horario y su calendario. Usted solo elige cómo se ven.',
  'builder.gallery.add': ({ name }: { name: string }) => `Añadir ${name}`,
  'builder.gallery.fromData': 'con sus datos',
  'builder.starter.question': '¿Una pregunta que se hace?',
  'builder.starter.answer': 'Su respuesta.',
  'builder.starter.who': 'Un cliente',
  'builder.starter.quote': 'Aquí van sus palabras.',
  'builder.starter.feature': ({ n }: { n: number }) => `Punto ${String(n)}`,
  'builder.starter.featureText': 'Una línea breve sobre ello.',
  'builder.starter.panel': ({ n }: { n: number }) => `Título ${String(n)}`,
  'builder.starter.panelText': 'Una línea sobre esta imagen.',
  'builder.starter.plan': ({ n }: { n: number }) => `Plan ${String(n)}`,
  'builder.starter.body': 'Unas frases con sus palabras.',
  'builder.starter.notice': 'Una línea para sus visitantes.',

  /* ---- aspecto ------------------------------------------------------ */
  'builder.look.title': 'Aspecto',
  'builder.look.presets': 'Estilo',
  'builder.look.open': 'Colores, tipografía y formas…',
  'builder.look.drawer': 'Colores, tipografía y formas',
  'builder.look.words': 'Nombre y lema',
  'builder.look.siteTitleHint': 'Vacío usa el nombre del negocio.',
  'builder.look.presetSays': ({ value }: { value: string }) => `El del estilo: ${value}.`,

  /* ---- páginas ------------------------------------------------------ */
  'builder.pages.newTitle': 'Una página nueva',
  'builder.pages.slugHint': ({ slug }: { slug: string }) =>
    `Su dirección: /p/${slug}. Minúsculas, números y guiones.`,
  'builder.pages.titleOf': ({ slug }: { slug: string }) => `Título de /p/${slug}`,
  'builder.pages.open': 'Ordenar esta página',
  'builder.pages.new': '+ Página nueva…',
  'builder.pages.bar': 'Barra de menú',

  /* ---- el lienzo ---------------------------------------------------- */
  'builder.canvas.title': 'Su sitio, como se vería el borrador',
  'builder.canvas.connecting': 'Abriendo su sitio…',
  'builder.canvas.offTitle': 'Ahora mismo no se puede mostrar la imagen en vivo de su sitio.',
  'builder.canvas.noAddress':
    'Esta consola no sabe desde dónde se sirve su sitio, así que no hay nada que enmarcar.',
  'builder.canvas.failed': ({ reason }: { reason: string }) =>
    `No se cargó: ${REASONS_ES[reason] ?? reason}.`,
  'builder.canvas.keepGoing':
    'La lista de secciones y el panel de la derecha siguen funcionando, y no se pierde nada: los cambios se guardan en este ordenador hasta que publique.',
  'builder.canvas.retry': 'Intentar de nuevo',
  'builder.canvas.editRefused': 'Ese texto no se puede cambiar aquí: use el panel de la derecha.',
  'builder.canvas.external': 'Los enlaces a otros sitios no se abren en el editor.',
  'builder.canvas.noSuchPage': 'Ese enlace lleva a una página que este borrador no tiene.',
  'builder.canvas.renderFailed':
    'El sitio no pudo dibujar ese cambio; muestra la última versión que pudo.',

  /* ---- publicar, versiones, plantillas, descartar ------------------- */
  'builder.publish.open': 'Publicar',
  'builder.publish.title': ({ name }: { name: string }) => `¿Publicar el sitio de ${name}?`,
  'builder.publish.checking': 'Revisando la página como lo hará la publicación…',
  'builder.publish.refused': 'Todavía no se puede publicar:',
  'builder.publish.clean': 'Revisado: nada que arreglar.',
  'builder.publish.warnings': 'Se publicará, con estas notas:',
  'builder.publish.cleaned': ({ count }: { count: number }) =>
    `Se limpiará${count === 1 ? '' : 'n'} ${String(count)} ${plural(count, 'bloque de texto con formato', 'bloques de texto con formato')}:`,
  'builder.publish.cleanedZone': ({ removed }: { removed: string }) => `se quita: ${removed}`,
  'builder.publish.note': 'Una nota breve sobre este cambio',
  'builder.publish.noteHint': 'Se guarda con la versión, para que quien la restaure sepa qué era.',
  'builder.publish.consequence':
    'Cada visitante lo verá al cargar la página, sin nada que compilar ni esperar. La versión que sustituye se conserva y se puede restaurar.',
  'builder.publish.go': 'Publicar ahora',
  'builder.versions.open': 'Versiones',
  'builder.versions.title': 'Versiones',
  'builder.versions.live': 'En línea ahora',
  'builder.versions.replacesDraft': 'Sustituye los cambios sin publicar del editor.',
  'builder.templates.lead':
    'Una plantilla sustituye todo el borrador: cada página y la barra de menú. Nada cambia en su sitio hasta que publique, y Deshacer devuelve el borrador.',
  'builder.templates.pages': ({ pages }: { pages: string }) => `Trae las páginas: ${pages}.`,
  'builder.templates.consequence':
    'Todo el borrador se sustituye por la plantilla. Nada cambia en su sitio hasta que publique, y Deshacer devuelve el borrador.',
  'builder.discard.title': '¿Descartar los cambios sin publicar?',
  'builder.discard.consequence':
    'El editor vuelve a lo publicado y se borra el borrador de este ordenador. Deshacer lo devuelve mientras no salga.',
  'builder.discard.go': 'Descartarlos',
};
